/**
 * Context Passing Service for the Learning Loop
 *
 * This service extracts context from coaching sessions and passes it
 * to simulations to create a connected learning experience.
 */

import { supabase } from '@/integrations/supabase/client';
import { captureCoachingError } from '@/lib/sentry';

export interface CoachingContext {
  sessionId: string;
  templateId: string;
  templateName: string;
  // Key extracted information from coaching
  prospectName?: string;
  companyName?: string;
  industry?: string;
  roleTitle?: string;
  objectives?: string[];
  keyTalkingPoints?: string[];
  anticipatedObjections?: string[];
  readinessScore?: number;
  documentContent?: any;
  // For simulation configuration
  recommendedScenarios?: string[];
}

export interface LinkedSimulation {
  id: string;
  name: string;
  type: string;
  difficulty?: string;
  description?: string;
  estimatedDuration?: string;
  imageUrl?: string;
  platform: string;
}

/**
 * Extract context from a coaching session document
 */
export function extractContextFromCoachingSession(session: any): CoachingContext {
  const documentContent = session.document_content || {};
  const sections = documentContent.sections || [];

  // Extract key information from document sections
  let prospectName = '';
  let companyName = '';
  let objectives: string[] = [];
  let keyTalkingPoints: string[] = [];
  let anticipatedObjections: string[] = [];

  // Parse sections to extract relevant data
  for (const section of sections) {
    const sectionName = (section.name || section.id || '').toLowerCase();
    const content = section.content;

    if (sectionName.includes('prospect') || sectionName.includes('contact')) {
      if (typeof content === 'string') {
        prospectName = content;
      } else if (Array.isArray(content)) {
        const nameEntry = content.find((item: any) =>
          item.key?.toLowerCase().includes('name') || item.label?.toLowerCase().includes('name')
        );
        if (nameEntry) prospectName = nameEntry.value || '';

        const companyEntry = content.find((item: any) =>
          item.key?.toLowerCase().includes('company') || item.label?.toLowerCase().includes('company')
        );
        if (companyEntry) companyName = companyEntry.value || '';
      }
    }

    if (sectionName.includes('objective') || sectionName.includes('goal')) {
      if (typeof content === 'string') {
        objectives = [content];
      } else if (Array.isArray(content)) {
        objectives = content.map((item: any) => typeof item === 'string' ? item : item.value || item.text || '');
      }
    }

    if (sectionName.includes('talking') || sectionName.includes('key point')) {
      if (typeof content === 'string') {
        keyTalkingPoints = [content];
      } else if (Array.isArray(content)) {
        keyTalkingPoints = content.map((item: any) => typeof item === 'string' ? item : item.value || item.text || '');
      }
    }

    if (sectionName.includes('objection')) {
      if (typeof content === 'string') {
        anticipatedObjections = [content];
      } else if (Array.isArray(content)) {
        anticipatedObjections = content.map((item: any) => typeof item === 'string' ? item : item.value || item.text || '');
      }
    }
  }

  return {
    sessionId: session.session_id,
    templateId: session.template_id,
    templateName: session.coaching_templates?.name || 'Coaching Session',
    prospectName,
    companyName,
    objectives,
    keyTalkingPoints,
    anticipatedObjections,
    readinessScore: session.readiness_score,
    documentContent: session.document_content
  };
}

/**
 * Find simulations linked to a coaching template
 */
export async function findLinkedSimulations(coachingTemplateId: string): Promise<LinkedSimulation[]> {
  // Find simulations that have this coaching template as their preparation
  const { data, error } = await supabase
    .from('simulation_agents')
    .select('id, name, type, difficulty, backstory, estimated_duration, image_url, platform')
    .eq('coaching_template_id', coachingTemplateId)
    .eq('status', 'active')
    .order('name');

  if (error) {
    console.error('Error finding linked simulations:', error);
    return [];
  }

  return (data || []).map((sim: any) => ({
    id: sim.id,
    name: sim.name,
    type: sim.type,
    difficulty: sim.difficulty,
    description: sim.backstory,
    estimatedDuration: sim.estimated_duration,
    imageUrl: sim.image_url,
    platform: sim.platform
  }));
}

/**
 * Map coaching categories to relevant simulation agent types
 */
type SimulationType = 'coach' | 'customer' | 'examiner' | 'patient';

const CATEGORY_TO_SIMULATION_TYPES: Record<string, SimulationType[]> = {
  'sales': ['customer'],
  'medical': ['patient'],
  'leadership': ['customer'],
  'customer_service': ['customer'],
  'healthcare': ['patient'],
  'pharma': ['patient'],
  'default': ['customer', 'patient']
};

/**
 * Find simulations by category that match the coaching template category
 */
export async function findRelatedSimulations(
  coachingCategory: string,
  companyId?: string | null,
  limit: number = 5
): Promise<LinkedSimulation[]> {
  // Map category to relevant simulation types
  const normalizedCategory = coachingCategory?.toLowerCase().trim() || 'default';
  const relevantTypes = CATEGORY_TO_SIMULATION_TYPES[normalizedCategory] 
    || CATEGORY_TO_SIMULATION_TYPES['default'];

  let query = supabase
    .from('simulation_agents')
    .select('id, name, type, difficulty, backstory, estimated_duration, image_url, platform, category')
    .eq('status', 'active')
    .in('type', relevantTypes)
    .limit(limit);

  // Filter by company if provided, but also include global agents
  if (companyId) {
    query = query.or(`company_id.eq.${companyId},company_id.is.null`);
  }

  // Order by most recently created
  query = query.order('created_at', { ascending: false });

  const { data, error } = await query;

  if (error) {
    console.error('Error finding related simulations:', error);
    return [];
  }

  return (data || []).map((sim: any) => ({
    id: sim.id,
    name: sim.name,
    type: sim.type,
    difficulty: sim.difficulty,
    description: sim.backstory,
    estimatedDuration: sim.estimated_duration,
    imageUrl: sim.image_url,
    platform: sim.platform
  }));
}

/**
 * Store coaching context for simulation use
 * This stores the context so the simulation can access preparation details
 */
export async function storeCoachingContextForSimulation(
  coachingSessionId: string,
  context: CoachingContext
): Promise<{ success: boolean; contextId?: string; error?: string }> {
  try {
    // Store context in session storage for immediate use
    sessionStorage.setItem(`coaching_context_${coachingSessionId}`, JSON.stringify(context));

    return { success: true, contextId: coachingSessionId };
  } catch (error: any) {
    console.error('Error storing coaching context:', error);
    captureCoachingError(error, { sessionId: coachingSessionId, phase: 'store_context' });
    return { success: false, error: error.message };
  }
}

/**
 * Retrieve coaching context for a simulation session
 */
export function getCoachingContextForSimulation(coachingSessionId: string): CoachingContext | null {
  try {
    const stored = sessionStorage.getItem(`coaching_context_${coachingSessionId}`);
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Create a simulation session with coaching context
 */
export async function createLinkedSimulationSession(
  simulationId: string,
  coachingSessionId: string,
  userId: string,
  authUserId: string,
  companyId?: string | null
): Promise<{ success: boolean; sessionId?: string; error?: string }> {
  try {
    const sessionId = crypto.randomUUID();

    const { error } = await supabase
      .from('simulation_evaluations')
      .insert({
        id: crypto.randomUUID(),
        session_id: sessionId,
        agent_id: simulationId,
        user_id: userId,
        auth_user_id: authUserId,
        company_id: companyId,
        coaching_session_id: coachingSessionId,
        is_practice: true,
        is_submitted: false,
        created_at: new Date().toISOString()
      });

    if (error) throw error;

    return { success: true, sessionId };
  } catch (error: any) {
    console.error('Error creating linked simulation session:', error);
    captureCoachingError(error, { phase: 'create_linked_session' });
    return { success: false, error: error.message };
  }
}

/**
 * Get debrief templates for a given coaching template category
 */
export async function getDebriefTemplate(category: string = 'sales'): Promise<any | null> {
  const { data, error } = await supabase
    .from('coaching_templates')
    .select('*')
    .ilike('name', '%debrief%')
    .ilike('category', `%${category}%`)
    .limit(1)
    .single();

  if (error || !data) {
    // Fall back to first debrief template
    const { data: fallback } = await supabase
      .from('coaching_templates')
      .select('*')
      .ilike('name', '%debrief%')
      .limit(1)
      .single();

    return fallback;
  }

  return data;
}
